/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import CampaignModel, { CampaignType } from 'cio/models/campaign';
import { action } from '@ember/object';
import { CampaignRestartModes } from 'cio/models/campaign';
import { inject as service } from '@ember/service';
import AuthService from 'cio/services/auth';

interface CampaignsTriggerSetupFrequencyControlsArgs {
  campaign: CampaignModel;
  type: CampaignType;
  disabled: boolean;
  restartMode: string;
  restartMinInterval: number;
  onUpdateRestartMode: (restartMode: string) => unknown;
  onUpdateInterval: (interval: number) => unknown;
}

const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = SECONDS_IN_MINUTE * 60;
const SECONDS_IN_DAY = SECONDS_IN_HOUR * 24;

const NOUN_PLACEHOLDER = 'NOUN_PLACEHOLDER';
const FREQUENCY_OPTION_NOUNS = {
  [CampaignType.Form]: 'form submission',
  [CampaignType.Transactional]: 'event',
};

export type TimeFrameUnitType = 'minute' | 'hour' | 'day' | 'week';

type BaseFrequencyTypeOption = {
  value: string;
  label: string;
  description: string;
  timeSelectorLabel?: string;
  timeFrameUnits?: TimeFrameUnitType[];
  minSeconds?: number;
};

type EventFrequencyTypeOption = BaseFrequencyTypeOption & {
  value: 'first' | 'once_within';
  interval: number | null;
};

type SegmentFrequencyTypeOption = BaseFrequencyTypeOption & {
  value: CampaignRestartModes.Interval | CampaignRestartModes.Rematch;
};

type FrequencyTypeOption = EventFrequencyTypeOption | SegmentFrequencyTypeOption;

const FREQUENCY_TYPE_OPTION_FIRST: EventFrequencyTypeOption = Object.freeze({
  interval: -1,
  value: 'first',
  label: 'Once ever',
  description: `This campaign will only trigger the first time the ${NOUN_PLACEHOLDER} is received for each person, after the campaign is published.`,
});

const FREQUENCY_TYPE_OPTION_ONCE_WITHIN: EventFrequencyTypeOption = Object.freeze({
  interval: null,
  value: 'once_within',
  label: 'Once within a time period',
  description: `This campaign will only trigger the first time the ${NOUN_PLACEHOLDER} is received during a specific time period.`,
  timeSelectorLabel: 'How much time should pass before people are eligible to enter again?',
  timeframeUnits: ['hour', 'day', 'week'],
  minSeconds: SECONDS_IN_HOUR,
});

const FREQUENCY_TYPE_OPTION_REMATCH: SegmentFrequencyTypeOption = Object.freeze({
  value: CampaignRestartModes.Rematch,
  label: 'Every re-match',
  description: 'A person must stop matching the trigger conditions in order to re-match.',
  timeSelectorLabel: 'What is the minimum time that should pass before the person is eligible to re-match?',
  timeframeUnits: ['minute', 'hour', 'day', 'week'],
  minSeconds: SECONDS_IN_MINUTE,
});

const FREQUENCY_TYPE_OPTION_INTERVAL: SegmentFrequencyTypeOption = Object.freeze({
  value: CampaignRestartModes.Interval,
  label: 'At fixed intervals',
  description: 'People will re-enter at set intervals as long as they still match the trigger and filter conditions.',
  timeSelectorLabel: 'How much time should pass between entry and re-entry?',
  timeframeUnits: ['day', 'week'],
  minSeconds: SECONDS_IN_DAY,
});

export default class CampaignsTriggerSetupFrequencyControls extends Component<CampaignsTriggerSetupFrequencyControlsArgs> {
  @service auth!: AuthService;

  get frequencyTypeOptions(): FrequencyTypeOption[] {
    if (this.args.type === CampaignType.Behavioral) {
      return [FREQUENCY_TYPE_OPTION_REMATCH, FREQUENCY_TYPE_OPTION_INTERVAL];
    }

    if (this.args.type === CampaignType.Transactional || this.args.type === CampaignType.Form) {
      let noun = FREQUENCY_OPTION_NOUNS[this.args.type];
      return [FREQUENCY_TYPE_OPTION_FIRST, FREQUENCY_TYPE_OPTION_ONCE_WITHIN].map((o) => ({
        ...o,
        description: o.description.replace(NOUN_PLACEHOLDER, noun),
      }));
    }

    return [];
  }

  get selectedFrequencyType() {
    if (this.args.type === CampaignType.Behavioral) {
      return this.frequencyTypeOptions.find((o) => this.args.restartMode === o.value)?.value;
    }

    if (this.args.type === CampaignType.Transactional || this.args.type === CampaignType.Form) {
      if (this.args.restartMinInterval === FREQUENCY_TYPE_OPTION_FIRST.interval) {
        return FREQUENCY_TYPE_OPTION_FIRST.value;
      }

      return FREQUENCY_TYPE_OPTION_ONCE_WITHIN.value;
    }

    return null;
  }

  get selectedFrequencyPeriodSeconds() {
    return this.args.restartMinInterval > 0 ? this.args.restartMinInterval : null;
  }

  get showConditionalTooltip() {
    return this.args.disabled && this.args.type === CampaignType.Behavioral;
  }

  get isEditingFrequencySettings() {
    if (this.args.type === CampaignType.Behavioral) {
      return this.args.restartMode !== CampaignRestartModes.None;
    }

    if (this.args.type === CampaignType.Transactional || this.args.type === CampaignType.Form) {
      return this.args.restartMinInterval !== 0;
    }

    return false;
  }

  @action
  toggleFrequencyLimit(active: boolean) {
    if (this.args.type === CampaignType.Behavioral) {
      this.args.onUpdateRestartMode(active ? CampaignRestartModes.Rematch : CampaignRestartModes.None);

      // When toggling the switch on for the first time, make sure to set the
      // value to the default of 1 day
      if (active && this.args.restartMinInterval === 0) {
        this.args.onUpdateInterval(SECONDS_IN_DAY);
      }
    }

    if (this.args.type === CampaignType.Transactional || this.args.type === CampaignType.Form) {
      this.args.onUpdateInterval(active ? (FREQUENCY_TYPE_OPTION_FIRST.interval as number) : 0);
    }
  }

  @action
  setFrequencyType(type: string) {
    if (this.args.type === CampaignType.Behavioral) {
      if (this.args.restartMode === type) {
        return;
      }

      this.args.onUpdateRestartMode(type);
      this.args.onUpdateInterval(SECONDS_IN_DAY);
    }

    if (this.args.type === CampaignType.Transactional || this.args.type === CampaignType.Form) {
      if (type === FREQUENCY_TYPE_OPTION_ONCE_WITHIN.value) {
        this.args.onUpdateInterval(SECONDS_IN_DAY);
      } else {
        this.args.onUpdateInterval(FREQUENCY_TYPE_OPTION_FIRST.interval as number);
      }
    }
  }
}
